import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { brokerOutreachApi, type BrokerOutreachStateDto } from "@/api/brokerOutreach";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { communicationKeys } from "@/pages/communications/hooks/useCommunications";

/**
 * The Broker intro tab's one read: the readiness strip, the sender, the field
 * kit and every card's status and saved draft.
 *
 * <p>🔴 <b>Refetches on EVERY window focus (D24 / R6).</b> FIND AGENT opens the
 * research workspace in a NEW TAB; when the consultant comes back, the strip
 * must show what they found without a reload. `"always"`, not `true`: the app's
 * client turns focus refetching off and holds data fresh for five minutes
 * (`main.tsx`), and `true` refetches only STALE data — so a consultant back
 * from the research tab inside five minutes would see the old strip.</p>
 */
export function useBrokerOutreachState(submissionId: string) {
  return useQuery({
    queryKey: buyerSubmissionKeys.brokerOutreach(submissionId),
    queryFn: () => brokerOutreachApi.get(submissionId),
    enabled: Boolean(submissionId),
    refetchOnWindowFocus: "always",
  });
}

/**
 * Every broker-outreach write returns the fresh state. This puts it in the
 * cache at once and invalidates the submission prefix (the send history, the
 * email card) — and, for a write that logged something, the Communications
 * feeds the entry landed on (buyer, submission, listing).
 */
export function useInvalidateWithOutreachState(submissionId: string) {
  const qc = useQueryClient();
  return useCallback(
    (state: BrokerOutreachStateDto, opts?: { logged?: boolean }) => {
      qc.setQueryData(buyerSubmissionKeys.brokerOutreach(submissionId), state);
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      if (opts?.logged) void qc.invalidateQueries({ queryKey: communicationKeys.all });
    },
    [qc, submissionId],
  );
}
